/**
 * Auth routes.
 *
 * Deliberately not a router library. The app has exactly eight addressable
 * places, and three of them are reached from a link in an email — which is the
 * real constraint here. The backend already decided what those links look like:
 *
 *   HEADLESS_FRONTEND_URLS = {
 *     account_confirm_email:          {FRONTEND_URL}/auth/verify-email/{key}
 *     account_reset_password:         {FRONTEND_URL}/auth/reset-password
 *     account_reset_password_from_key: {FRONTEND_URL}/auth/reset-password/{key}
 *     account_signup:                 {FRONTEND_URL}/auth/create-account
 *   }
 *
 * So these paths are not a design choice this file is free to make. They are a
 * contract with `config/settings.py`, and `routes.test.ts` asserts they still
 * match it. A pretty URL scheme that breaks every confirmation email is worse
 * than an ugly one that works.
 *
 * The key travels in the *path*, not a query string, because query strings end
 * up in referrer headers and server logs. It is still only a claim: the screen
 * submits it and the server decides. See `verifyEmail` / `resetPassword`.
 */

/**
 * Where inside the app a learner is.
 *
 * Held apart from `Route` because these are the destinations navigation offers,
 * while `Route` also covers the auth journey. Both navigation surfaces read this
 * one value, so a bottom bar and a menu cannot disagree about where you are.
 */
export type Destination =
  | 'home'
  | 'write'
  | 'manuscript'
  | 'fib'
  | 'modes'
  | 'intervals'
  | 'learn'
  | 'classroom'
  | 'chord-legend';

export const DESTINATIONS: Destination[] = [
  'home',
  'write',
  'manuscript',
  'fib',
  'modes',
  'intervals',
  'learn',
  'classroom',
  'chord-legend',
];

/**
 * A chord the learner asked to be built, carried in the URL.
 *
 * The Chord Legend's "Build in FIB" hands over a root and a quality, and the
 * only honest place to put them is the address. A module-level variable or a
 * context set on the way out would work until the learner pressed Back — at
 * which point FIB would rebuild from a hand-off that had already been consumed,
 * or not rebuild at all. In the URL it is simply part of where they are.
 */
export interface BuildRequest {
  /** Pitch class of the root, 0-11. The Legend's root selector speaks the same. */
  rootClass: number;
  /** A `ChordQuality.id`. Validated by the screen, not here. */
  qualityId: string;
  /**
   * How to spell the root.
   *
   * A pitch class is not a note. Class 1 is C♯ or D♭ depending on which the
   * learner chose, and without this the Legend's C♯ major arrived in FIB as
   * D♭ major, because FIB initialised its own preference to flat. Two different
   * chords on paper, silently swapped in transit.
   *
   * Optional so that links made before this existed still work; they open on
   * FIB's own default, which is what they did anyway.
   */
  spelling?: 'sharp' | 'flat';
  /**
   * The rest of what FIB is showing.
   *
   * FIB is unmounted the moment the learner opens the Chord Legend — routes
   * swap the whole screen — so anything held only in component state is gone
   * when they press Back. The URL is the one place that survives an unmount, a
   * refresh, and the back/forward buttons, so the parts of the exercise a
   * learner would notice losing live here.
   */
  mode?: 'choose' | 'cheat' | 'practice';
  clef?: string;
  /** 0 = root position. Changes which chord is drawn, so it is worth keeping. */
  inversion?: number;
}

export type Route =
  /** The app itself, at one of its destinations. Guests may use all of them. */
  | { name: 'app'; destination: Destination; build?: BuildRequest }
  | { name: 'sign-in' }
  | { name: 'create-account' }
  /** No key: "check your email". With a key: submit it and find out. */
  | { name: 'verify-email'; key: string | null }
  | { name: 'forgot-password' }
  | { name: 'reset-password'; key: string | null }
  | { name: 'account' };

export type RouteName = Route['name'];

/** Every route that is part of the signed-out journey. */
export const AUTH_ROUTES: RouteName[] = [
  'sign-in',
  'create-account',
  'verify-email',
  'forgot-password',
  'reset-password',
];

export function isAuthRoute(route: Route): boolean {
  return AUTH_ROUTES.includes(route.name);
}

/**
 * A key is opaque to us — allauth chooses the alphabet — so this only rejects
 * what cannot be a key at all, rather than pretending to validate one.
 */
function cleanKey(raw: string | undefined): string | null {
  if (!raw) return null;
  const key = decodeURIComponent(raw).trim();
  return key.length > 0 && key.length <= 512 ? key : null;
}

const SPELLINGS = ['sharp', 'flat'] as const;
const MODES = ['choose', 'cheat', 'practice'] as const;
const CLEFS_IN_URL = ['treble', 'bass', 'alto', 'tenor'];

/**
 * `?root=3&quality=maj7&spelling=sharp` -> a build request.
 *
 * Root and quality are required together; everything else is optional and each
 * optional field is dropped on its own if it is not a value this app knows.
 * A hand-edited or truncated URL has to open the screen, never break it.
 */
function parseBuild(search: string | undefined): BuildRequest | undefined {
  if (!search) return undefined;
  const params = new URLSearchParams(search);
  const rawRoot = params.get('root');
  const qualityId = params.get('quality');
  // Both halves, or neither. A missing root used to become C, because
  // `Number(null)` is 0 — so a truncated link quietly built the wrong chord.
  if (!qualityId || rawRoot === null) return undefined;
  const root = Number(rawRoot);
  if (!Number.isInteger(root) || root < 0 || root > 11) return undefined;

  const build: BuildRequest = { rootClass: root, qualityId };

  const spelling = params.get('spelling');
  if (spelling && (SPELLINGS as readonly string[]).includes(spelling)) {
    build.spelling = spelling as BuildRequest['spelling'];
  }
  const mode = params.get('mode');
  if (mode && (MODES as readonly string[]).includes(mode)) {
    build.mode = mode as BuildRequest['mode'];
  }
  const clef = params.get('clef');
  if (clef && CLEFS_IN_URL.includes(clef)) build.clef = clef;

  const inversion = Number(params.get('inversion'));
  if (params.get('inversion') !== null && Number.isInteger(inversion) && inversion >= 0 && inversion <= 6) {
    build.inversion = inversion;
  }

  return build;
}

export function parseRoute(pathname: string, search?: string): Route {
  // Tolerate a trailing slash and a base path of "/".
  const segments = pathname.split('/').filter(Boolean);

  if (segments[0] === 'account' && segments.length === 1) return { name: 'account' };

  if (segments[0] !== 'auth') {
    const first = segments[0] as Destination | undefined;
    if (first && DESTINATIONS.includes(first)) {
      const build = parseBuild(search);
      return build ? { name: 'app', destination: first, build } : { name: 'app', destination: first };
    }
    // Anything unrecognised lands on Home rather than a dead end.
    return { name: 'app', destination: 'home' };
  }

  switch (segments[1]) {
    case 'sign-in':
      return { name: 'sign-in' };
    case 'create-account':
      return { name: 'create-account' };
    case 'verify-email':
      return { name: 'verify-email', key: cleanKey(segments[2]) };
    case 'forgot-password':
      return { name: 'forgot-password' };
    case 'reset-password':
      return { name: 'reset-password', key: cleanKey(segments[2]) };
    default:
      // An unknown /auth/* path is not an error page. A learner who mistypes, or
      // an old link from a previous build, lands on Home — which works signed
      // out — rather than on a dead end.
      return { name: 'app', destination: 'home' };
  }
}

export function pathFor(route: Route): string {
  switch (route.name) {
    case 'app': {
      const base = route.destination === 'home' ? '/' : `/${route.destination}`;
      if (!route.build) return base;
      const { rootClass, qualityId, spelling, mode, clef, inversion } = route.build;
      const query = new URLSearchParams({
        root: String(rootClass),
        quality: qualityId,
      });
      // Only what was actually set: an absent field means "whatever the screen
      // defaults to", and writing it out would make two equivalent URLs differ.
      if (spelling) query.set('spelling', spelling);
      if (mode) query.set('mode', mode);
      if (clef) query.set('clef', clef);
      if (inversion !== undefined) query.set('inversion', String(inversion));
      return `${base}?${query.toString()}`;
    }
    case 'account':
      return '/account';
    case 'verify-email':
      return route.key ? `/auth/verify-email/${encodeURIComponent(route.key)}` : '/auth/verify-email';
    case 'reset-password':
      return route.key
        ? `/auth/reset-password/${encodeURIComponent(route.key)}`
        : '/auth/reset-password';
    default:
      return `/auth/${route.name}`;
  }
}

/**
 * Drop the key from the address bar once it has been spent.
 *
 * A confirmation link is a single-use credential. Leaving it in the URL means it
 * is in the history of a possibly shared iPad, and it is what gets copied when
 * someone shares "the page I'm stuck on". `replaceState` also stops the back
 * button from resubmitting a key the server has already consumed.
 */
export function forgetKeyInAddressBar(route: Route): void {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  if (route.name !== 'verify-email' && route.name !== 'reset-password') return;
  if (!route.key) return;
  window.history.replaceState(null, '', pathFor({ ...route, key: null }));
}
